import React, { useEffect, useState } from "react";
import Modal from "../common/Modal";

const setCookie = (name, value, days) => {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(
    JSON.stringify(value),
  )}; expires=${expires}; path=/; SameSite=Lax`;
};

const getCookie = (name) => {
  const cookies = document.cookie.split("; ").reduce((acc, curr) => {
    const [key, val] = curr.split("=");
    acc[key] = val;
    return acc;
  }, {});
  if (!cookies[name]) return null;
  try {
    return JSON.parse(decodeURIComponent(cookies[name]));
  } catch {
    return null;
  }
};

const deleteCookie = (name) => {
  document.cookie = `${name}=; Max-Age=0; path=/`;
};

const COOKIE_NAME = "cookie_consent";
const COOKIE_VERSION = "1.0";
const CONSENT_EXPIRY_DAYS = 365;

const DEFAULT_PREFS = {
  essential: true,
  analytics: false,
  marketing: false,
  preferences: false,
};

const COOKIE_TYPES = [
  {
    key: "essential",
    label: "Essential cookies",
    duration: "Session",
    required: true,
    description:
      "Required for core site functionality like logins and security.",
  },
  {
    key: "analytics",
    label: "Analytics cookies",
    duration: "365 days",
    required: false,
    description: "Help us understand how visitors use the site anonymously.",
  },
  {
    key: "marketing",
    label: "Marketing cookies",
    duration: "90 days",
    required: false,
    description: "Used for ads and campaign tracking across platforms.",
  },
  {
    key: "preferences",
    label: "Preference cookies",
    duration: "180 days",
    required: false,
    description: "Remember your settings and personalisation choices.",
  },
];

function Toggle({ checked, disabled, onChange }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onChange}
      disabled={disabled}
      aria-pressed={checked}
      className={`relative inline-flex items-center h-5 w-10 rounded-full transition-colors duration-200 !outline-none ${
        disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
      } ${checked ? "bg-[#1E9ABC]" : "bg-[#17171780]"}`}
    >
      <span
        className={`inline-block w-3.5 h-3.5 bg-white rounded-full shadow transition-transform duration-200 ${
          checked ? "translate-x-[22px]" : "translate-x-[4px]"
        }`}
      />
    </button>
  );
}

const CookieBanner = ({ cookiePolicyUrl = "/cookie-policies" }) => {
  const [visible, setVisible] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const [savedMessage, setSavedMessage] = useState("");
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);

  useEffect(() => {
    const stored = getCookie(COOKIE_NAME);
    if (!stored || stored.version !== COOKIE_VERSION) {
      setVisible(true);
      setSaved(false);
    } else {
      setPrefs({
        essential: true,
        analytics: stored.analytics ?? false,
        marketing: stored.marketing ?? false,
        preferences: stored.preferences ?? false,
      });
      setSaved(true);
      setSavedMessage("Your cookie preferences are saved.");
    }
  }, []);

  const loadAllowedServices = (p) => {
    if (p.analytics) console.log("[Cookie] Analytics enabled");
    if (p.marketing) console.log("[Cookie] Marketing enabled");
    if (p.preferences) console.log("[Cookie] Preferences enabled");
  };

  const saveConsent = (updatedPrefs, message) => {
    const payload = {
      ...updatedPrefs,
      essential: true,
      version: COOKIE_VERSION,
      consentedAt: new Date().toISOString(),
    };
    setCookie(COOKIE_NAME, payload, CONSENT_EXPIRY_DAYS);
    setPrefs(payload);
    setSaved(true);
    setSavedMessage(message);
    setVisible(false);
    setPanelOpen(false);
    loadAllowedServices(payload);
  };

  const acceptAll = () =>
    saveConsent(
      { analytics: true, marketing: true, preferences: true },
      "All cookies accepted.",
    );

  const rejectAll = () =>
    saveConsent(
      { analytics: false, marketing: false, preferences: false },
      "Only essential cookies are active.",
    );

  const saveCustom = () =>
    saveConsent(prefs, "Your preferences have been saved.");

  const toggle = (key) => setPrefs((prev) => ({ ...prev, [key]: !prev[key] }));

  const resetConsent = () => {
    deleteCookie(COOKIE_NAME);
    setPrefs(DEFAULT_PREFS);
    setSaved(false);
    setSavedMessage("");
    setVisible(true);
    setPanelOpen(false);
  };

  if (!visible && !saved) return null;
  if (saved && !panelOpen) return null;

  // if (saved && !panelOpen) {
  //   return (
  //     <div className="fixed bottom-0 left-0 w-full z-50 bg-green-50 border-t border-green-200 px-6 py-3 flex items-center gap-3 text-sm text-green-800 font-medium">
  //       <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
  //       <span className="flex-1">{savedMessage}</span>
  //       <button
  //         onClick={() => setPanelOpen(true)}
  //         className="ml-auto text-xs text-gray-500 underline underline-offset-2 bg-transparent border-none cursor-pointer hover:text-gray-700 transition-colors"
  //       >
  //         Update preferences
  //       </button>
  //     </div>
  //   );
  // }

  return (
    <>
      <div className="fixed bottom-[30px] left-0 right-0 w-full z-50 bg-white border-t border-gray-200 max-w-[90%] rounded-[18px] mx-auto shadow-lg font-sans">
        <div className="px-6 py-5 shadow-[0 25px 50px -12px rgba(0, 0, 0, 0.25)] ">
          <div className="flex justify-between items-start gap-4">
            <div className="">
              <h2 className="lg:text-[20px] sm:text-[18px] font-semibold text-[#0A0A0A] ">
                We use cookies
              </h2>
              <p className=" sm:text-[14px] text-[12px] text-[#525252] mt-1 max-w-xl">
                We use essential cookies to keep this site working and optional
                ones to improve your experience.{" "}
                <a
                  href={cookiePolicyUrl}
                  className=" underline text-[#1E9ABC] "
                >
                  Read our cookie policy
                </a>
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={acceptAll}
                className="px-7 py-1.5 whitespace-nowrap rounded-[8px] bg-[#fff] border border-[#E5E5E5] text-[#0a0a0a] hover:bg-[#1E9ABC] sm:text-[14px] text-[12px] hover:text-[#FFf] "
              >
                Accept all
              </button>
              <button
                onClick={rejectAll}
                className="px-7 py-1.5 whitespace-nowrap rounded-[8px] bg-[#fff] border border-[#E5E5E5] text-[#0a0a0a] hover:bg-[#1E9ABC] sm:text-[14px] text-[12px] hover:text-[#FFf] "
              >
                Reject all
              </button>
              {panelOpen ? (
                ""
              ) : (
                <button
                  onClick={() => setPanelOpen((o) => !o)}
                  className={`px-7 py-1.5 whitespace-nowrap rounded-[8px] border border-[#E5E5E5]  sm:text-[14px] text-[12px]  ${
                    panelOpen
                      ? "bg-[#1E9ABC] text-[#FFF] border-blue-200"
                      : "bg-[#fff] text-[#0a0a0a]  hover:bg-[#1E9ABC] hover:text-[#FFf] "
                  }`}
                >
                  {panelOpen ? "Hide settings" : "Manage cookies"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      {panelOpen && (
        <Modal
          title=""
          className=""
          body={
            <div className="fixed inset-0 flex items-center justify-center z-50 px-4">
              <form className="bg-white shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] rounded-[14px] w-full max-w-[550px] shadow-lg sm:p-6 p-3 ">
                <div className="h-[calc(100vh-180px)]  overflow-auto">
                  <h2 className="lg:text-[22px] sm:text-[20px] text-[18px] text-[#1E9ABC] font-semibold ">
                    Cookie Preferences
                  </h2>
                  <p className="sm:text-[14px] text-[12px] text-[#525252] ">
                    Manage your cookie settings. You can enable or disable
                    different types of cookies below.
                  </p>
                  <div className="space-y-4 mt-5">
                    {COOKIE_TYPES.map((ct) => (
                      <div
                        key={ct.key}
                        className="flex items-center justify-between gap-4 p-3 hover:bg-[#FAFAFA] border border-[#E5E5E5] rounded-[12px] "
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-0.5">
                            <h2 className="sm:text-[16px] text-[#0A0A0A] font-medium">
                              {ct.label}
                            </h2>
                            {ct.required ? (
                              <span className="text-xs bg-green-50 text-green-700 border border-green-200 rounded px-1.5 py-0.5 font-medium">
                                Required
                              </span>
                            ) : (
                              <span className="text-xs bg-gray-50 text-gray-500 border border-gray-200 rounded px-1.5 py-0.5">
                                {ct.duration}
                              </span>
                            )}
                          </div>
                          <p className="sm:text-[14px] text-[12px] text-[#525252] ">
                            {ct.description}
                          </p>
                        </div>

                        <Toggle
                          checked={prefs[ct.key]}
                          disabled={ct.required}
                          onChange={() => toggle(ct.key)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="pt-4">
                  <div className="flex flex-wrap items-center justify-end gap-2 mt-4 pt-4 border-t border-gray-100">
                    <button
                      onClick={resetConsent}
                      className="text-xs px-4 py-2 rounded-lg bg-transparent text-red-500 border border-red-200 hover:bg-red-50 transition-colors cursor-pointer "
                    >
                      Reset all
                    </button>
                    <button
                      onClick={() => setPanelOpen(false)}
                      className="px-4 py-1.5 rounded-[8px] text-[#0A0A0A] sm:text-[14px] text-[12px] border border-[#E5E5E5] bg-[#FFF] hover:opacity-90"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={saveCustom}
                      className="px-4 py-1.5 rounded-[8px] text-[#FFF] sm:text-[14px] text-[12px] bg-[#1E9ABC] hover:opacity-90 "
                    >
                      Save preferences
                    </button>
                  </div>
                </div>
              </form>
            </div>
          }
          result={(e) => setPanelOpen((o) => !o)}
        />
      )}
    </>
  );
};

export default CookieBanner;
